import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { KnowledgeBaseApi } from '@/Domain/api/KnowledgeBase/KnowledgeBase.api';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { ErrorType } from '@/Domain/components/KnowledgeBaseArticles/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useArticleLimitCheck } from '@/Domain/hooks/KnowledgeBase/useArticleLimitCheck.hook';

type FileInput = {
  file: File;
};

const UNSUPPORTED_MEDIA_STATUS = 415;

export const useUploadArticles = () => {
  const queryClient = useQueryClient();
  const handleArticleLimitCheck = useArticleLimitCheck();
  const { capture } = usePostHogAnalytics();
  const { t } = useTranslation(['knowledge_base']);

  const mutation = useMutation<
    { article: ArticleDto },
    AxiosError<{ errors?: ErrorType<FileInput> }>,
    File
  >({
    mutationKey: [Endpoint.knowledgeBase.articles.uploadFile],
    mutationFn: KnowledgeBaseApi.uploadFile,
    onSuccess: ({ article }) => {
      queryClient.setQueryData<ArticleDto[]>(
        [QueryKey.knowledgeBase.articles.listOrFilter, '', []],
        (oldData = []) => [article, ...oldData],
      );

      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });
    },
    onError: (error) => {
      // A rejected upload never reaches the article store, so useArticleFileStatus can't see it.
      // Only the 415 body is safe to render — other failures carry backend detail (bucket names,
      // IAM principals) that must never reach a toast.
      const message =
        (error.response?.status === UNSUPPORTED_MEDIA_STATUS
          ? error.response?.data?.errors?.file?.[0]?.message
          : undefined) ?? t('knowledge_base:modals.upload_file.error');
      toast.error(message);
      capture(PostHogEventEnum.ArticleUploadFailed, {
        upload_type: 'file',
        failure_stage: 'upload_rejected',
        status_code: error.response?.status ?? null,
      });
    },
  });

  const handleUploadFile = async (file: File) => {
    try {
      mutation.mutate(file);
      return true;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      return false;
    }
  };

  const handleUploadFiles = async (files: File[], onComplete?: () => void) => {
    capture(PostHogEventEnum.ArticleUploadStarted, {
      upload_type: 'file',
      file_count: files.length,
    });
    try {
      handleArticleLimitCheck(async () => {
        await Promise.all(files.map((file) => handleUploadFile(file)));
        if (onComplete) {
          onComplete();
        }
      });
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  return {
    handleUploadFile,
    handleUploadFiles,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
